import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { searchFlat } from '../../../qwanyx-core/api-client';
import { DNDEditor } from '../../editor/DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { getTemplateCache } from '../services/templateCache';
import { 
  transformToIconData, 
  getSelectionStyle, 
  getBackgroundColor,
  createCardData 
} from '../utils/stackUtils';

/**
 * FlatView - Flat search results with infinite pagination
 * 
 * This component provides a flat list view of search results with:
 * - Infinite scroll pagination
 * - Search query support
 * - Domain filtering (di parameter)
 * - Template rendering for list items
 * - Selection management
 * - Faceting support
 * 
 * Props:
 * - rootId: Root ID to scope search within (optional)
 * - di: Domain ID for filtering (optional)
 * - query: Search query string
 * - fields: Fields to search in (default: all text fields)
 * - fuzzy: Enable fuzzy search
 * - facets: Facet fields to generate
 * - pageSize: Items per page (default: 50)
 * - onSelect: Selection callback
 * - selectedItems: Currently selected items
 * - itemTemplate: Template for rendering list items
 * - useCompact: Use compact rendering
 * - debug: Enable debug logging
 * - showDeleteButton: Show delete button (cross) on items (default: true)
 */

const FlatView = ({
  rootId = null,
  di = null,
  query = '',
  fields = [],
  fuzzy = false,
  facets = [],
  facetConfig = [], // Array of {field, label} objects for custom facet configuration
  pageSize = 50,
  onSelect,
  selectedItems = [],
  itemTemplate = null,
  templateProjection = [], // Fields to fetch for template variable replacement
  useCompact = true,
  debug = false,
  // UI props
  showSearchInfo = true,
  showLoadingState = true,
  showFacets = true,
  showFacetCounts = true,
  // Virtualization props
  itemHeight = 80,
  fixedHeight = false, // Force fixed height instead of dynamic calculation
  virtualized = true,
  overscanCount = 5,
  // Styling props
  itemGap = 0, // Gap between items in pixels
  showSeparator = false, // Show separator line between items
  separatorWidth = 1, // Separator line width in pixels
  separatorColor = "#e0e0e0", // Separator line color
  // Sorting props
  sortBy = 'title',
  sortOrder = 1,
  // Additional context
  extraContext = null,
  // Delete button visibility
  showDeleteButton = false
}) => {
  // State management
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [facetData, setFacetData] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Template state
  const [templateData, setTemplateData] = useState(null);
  const [processedTemplates, setProcessedTemplates] = useState(new Map());
  
  // Item sizes for variable height virtualization
  const [itemSizes, setItemSizes] = useState({});
  
  // Refs for infinite scroll and height measurement
  const loadingRef = useRef(null);
  const observerRef = useRef(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  
  // Height state for virtualization
  const [containerHeight, setContainerHeight] = useState(400);
  
  // Measure container height for react-window
  useEffect(() => {
    const measureHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newHeight = rect.height;
        if (newHeight !== containerHeight && newHeight > 0) {
          setContainerHeight(newHeight);
          if (debug) {
            console.log('[FlatView] Container height updated:', newHeight);
          }
        }
      }
    };
    
    // Use requestAnimationFrame to ensure DOM has updated
    const measureWithDelay = () => {
      requestAnimationFrame(() => {
        setTimeout(measureHeight, 100); // Slightly longer delay for UI transitions
      });
    };
    
    measureWithDelay();
    
    // Set up ResizeObserver for more accurate height tracking
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== containerHeight && newHeight > 0) {
            setContainerHeight(newHeight);
            if (debug) {
              console.log('[FlatView] ResizeObserver height update:', newHeight);
            }
          }
        }
      });
      resizeObserver.observe(containerRef.current);
    }
    
    // Fallback to window resize listener
    window.addEventListener('resize', measureWithDelay);
    
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', measureWithDelay);
    };
  }, [showSearchInfo, showFacets, showLoadingState, debug]); // Remove containerHeight to prevent infinite loop
  
  // Load template once when itemTemplate changes
  useEffect(() => {
    if (itemTemplate) {
      // Force refresh on mount to ensure fresh templates during development
      getTemplateCache().fetchTemplate(itemTemplate, true)
        .then(template => {
          setTemplateData(template);
          if (debug) {
            console.log('[FlatView] Template loaded with refresh:', itemTemplate);
          }
        })
        .catch(error => {
          console.error('[FlatView] Error loading template:', error);
        });
    } else {
      setTemplateData(null);
      setProcessedTemplates(new Map());
    }
  }, [itemTemplate, debug]);

  // Process templates for all items when templateData or items change
  useEffect(() => {
    if (templateData && items.length > 0) {
      const newProcessedTemplates = new Map();
      
      items.forEach(item => {
        try {
          const processedTemplate = getTemplateCache().replaceTemplateVariables(templateData, item);
          newProcessedTemplates.set(item._id, processedTemplate);
        } catch (error) {
          console.error(`[FlatView] Error processing template for item ${item._id}:`, error);
        }
      });
      
      setProcessedTemplates(newProcessedTemplates);
      
      if (debug) {
        console.log(`[FlatView] Processed templates for ${newProcessedTemplates.size} items`);
      }
    }
  }, [templateData, items, debug]);

  // Build projection from templateProjection config
  const buildProjection = useCallback(() => {
    if (!templateProjection || templateProjection.length === 0) {
      return null; // No projection, get all fields
    }

    const projection = {};
    
    // Always include system fields
    projection._id = 1;
    projection.qwantaType = 1;
    projection.di = 1;
    projection.ed = 1;
    projection.pa = 1;
    projection.vi = 1;
    projection.qwantum = 1; // Include qwantum for data payload
    
    // Add enabled fields from templateProjection
    templateProjection.forEach(field => {
      if (field.enabled && field.field) {
        // Support nested fields in qwantum with dot notation
        if (field.field.startsWith('qwantum.')) {
          projection[field.field] = 1;
        } else {
          // For top-level fields, check both root level and qwantum
          projection[field.field] = 1;
          projection[`qwantum.${field.field}`] = 1;
        }
      }
    });

    if (debug) {
      console.log('[FlatView] Built projection:', projection);
    }

    return projection;
  }, [templateProjection, debug]);

  // Search function
  const performSearch = useCallback(async (reset = false) => {
    if (loading) return;
    
    const skip = reset ? 0 : currentPage * pageSize;
    const projection = buildProjection();
    
    if (debug) {
      console.log('[FlatView] Performing search:', {
        rootId, di, query, fields, fuzzy, facets,
        skip, limit: pageSize, reset, projection
      });
    }
    
    setLoading(true);
    if (reset) {
      setError(null);
    }
    
    try {
      const result = await searchFlat(
        rootId, di, query, fields, fuzzy, facets,
        skip, pageSize, sortBy, sortOrder, projection
      );
      
      if (debug) {
        console.log('[FlatView] Search result:', result);
      }
      
      if (reset) {
        setItems(result.results);
        setCurrentPage(1);
      } else {
        setItems(prev => [...prev, ...result.results]);
        setCurrentPage(prev => prev + 1);
      }
      
      setFacetData(result.facets || {});
      setTotalCount(result.total);
      setHasMore(result.results.length === pageSize && (skip + result.results.length) < result.total);
      
    } catch (err) {
      console.error('[FlatView] Search error:', err);
      setError(err.message);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [rootId, di, query, fields, fuzzy, facets, pageSize, currentPage, loading, debug, sortBy, sortOrder, buildProjection]);

  // Initial search and reset on query change - add debug logging
  useEffect(() => {
    if (debug) {
      console.log('[FlatView] Query/config changed, resetting search:', { rootId, di, query, sortBy, sortOrder });
    }
    setItems([]);
    setCurrentPage(0);
    setHasMore(true);
    performSearch(true);
  }, [rootId, di, query, fields, fuzzy, facets, pageSize, sortBy, sortOrder, templateProjection]); // performSearch excluded to avoid infinite loop

  // Infinite scroll setup
  useEffect(() => {
    const currentLoadingRef = loadingRef.current;
    
    if (!currentLoadingRef) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          performSearch(false);
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(currentLoadingRef);
    observerRef.current = observer;
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, performSearch]);

  // Get processed template for an item - use useCallback to prevent unnecessary re-renders
  const getProcessedTemplateForItem = useCallback((itemId) => {
    return processedTemplates.get(itemId) || null;
  }, [processedTemplates]);

  // Calculate item heights based on template content
  const calculateItemHeights = useCallback(() => {
    const newSizes = {};
    
    items.forEach((item) => {
      if (fixedHeight) {
        // Force fixed height for all items
        newSizes[item._id] = itemHeight + itemGap;
      } else {
        const hasTemplate = processedTemplates.has(item._id);
        
        if (hasTemplate) {
          // For items with templates, estimate height based on content
          // This provides better height estimation for template content
          const templateContent = processedTemplates.get(item._id);
          let estimatedHeight = itemHeight;
          
          if (templateContent && templateContent.ROOT) {
            // Estimate height based on template structure
            // Count number of components in template to estimate height
            const componentCount = Object.keys(templateContent.ROOT.nodes || {}).length;
            
            if (useCompact) {
              // Compact mode: smaller base height with increment per component
              estimatedHeight = Math.max(80, 60 + componentCount * 20);
            } else {
              // Normal mode: larger base height with increment per component  
              estimatedHeight = Math.max(120, 80 + componentCount * 30);
            }
            
            // Cap maximum height to avoid extremely tall items
            estimatedHeight = Math.min(estimatedHeight, 300);
          } else {
            // Fallback for templates without clear structure
            estimatedHeight = useCompact ? 120 : 160;
          }
          
          newSizes[item._id] = estimatedHeight + itemGap;
        } else {
          // Default height for items without templates
          newSizes[item._id] = itemHeight + itemGap;
        }
      }
    });
    
    setItemSizes(newSizes);
    
    // Reset list size cache if the list exists
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [items, processedTemplates, useCompact, itemHeight, itemGap, showSeparator, fixedHeight]);

  // Get item size for VariableSizeList
  const getItemSize = useCallback((index) => {
    const item = items[index];
    return itemSizes[item?._id] || itemHeight;
  }, [items, itemSizes, itemHeight]);

  // Recalculate heights when items or templates change
  useEffect(() => {
    if (items.length > 0) {
      calculateItemHeights();
    }
  }, [items, processedTemplates, calculateItemHeights]);

  // Handle item selection - use useCallback to prevent unnecessary re-renders
  const handleItemSelect = useCallback((item, isShiftKey = false, isCtrlKey = false) => {
    if (onSelect) {
      onSelect(item._id, isShiftKey, isCtrlKey);
    }
  }, [onSelect]);

  // Virtualized row renderer
  const VirtualizedRow = ({ index, style, data }) => {
    const item = data.items[index];
    const isSelected = data.selectedItems.includes(item._id);
    const processedTemplate = data.getProcessedTemplateForItem(item._id);
    
    // Check if this is near the end and should trigger loading
    const isNearEnd = index >= data.items.length - data.overscanCount;
    const shouldLoadMore = isNearEnd && data.hasMore && !data.loading;
    
    // Trigger loading when near end
    if (shouldLoadMore) {
      // Use setTimeout to avoid triggering during render
      setTimeout(() => {
        data.performSearch(false);
      }, 0);
    }
    
    // Template rendering
    if (processedTemplate) {
      const hasGap = data.itemGap > 0;
      const hasSeparator = data.showSeparator && hasGap;
      
      return (
        <div style={style}>
          <div
            onClick={(e) => data.handleItemSelect(item, e.shiftKey, e.ctrlKey)}
            style={{
              ...getSelectionStyle(isSelected, false, true),
              backgroundColor: getBackgroundColor(isSelected, false),
              cursor: 'pointer',
              height: hasGap ? `calc(100% - ${data.itemGap}px)` : '100%', // Account for gap
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative' // Nécessaire pour la croix absolue
            }}
          >
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <QwantaProvider _id={item._id}>
                <DNDEditor
                  cardData={processedTemplate}
                  enabled={false}
                  readOnly={true}
                  stackMode={false}
                  compact={data.useCompact}
                />
              </QwantaProvider>
            </div>
            
            {/* Croix de suppression automatique par-dessus le template */}
            {data.showDeleteButton && (
              <div
                onClick={(e) => {
                  e.stopPropagation(); // Empêcher la sélection de l'item
                  if (data.extraContext && data.extraContext.deleteRequest) {
                    data.extraContext.deleteRequest(item._id);
                  } else {
                    console.log('deleteRequest non disponible');
                  }
                }}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  fontSize: '14px',
                  color: '#d32f2f',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                  e.target.style.transform = 'scale(1)';
                }}
                title="Supprimer cette demande"
              >
                ✕
              </div>
            )}
          </div>
          
          {/* Separator - centered in the gap */}
          {hasSeparator && (
            <div style={{
              height: data.itemGap,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '100%',
                height: data.separatorWidth,
                backgroundColor: data.separatorColor
              }} />
            </div>
          )}
        </div>
      );
    }
    
    // Default rendering
    const displayText = item.title || 
      (item.firstname && item.name ? `${item.firstname} ${item.name}` : 'Untitled');
    
    const hasGap = data.itemGap > 0;
    const hasSeparator = data.showSeparator && hasGap;
    
    return (
      <div style={style}>
        <div
          onClick={(e) => data.handleItemSelect(item, e.shiftKey, e.ctrlKey)}
          style={{
            ...getSelectionStyle(isSelected, false, true),
            backgroundColor: getBackgroundColor(isSelected, false),
            padding: '8px',
            borderRadius: '4px',
            cursor: 'pointer',
            border: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            height: hasGap ? `calc(100% - ${data.itemGap}px)` : 'calc(100% - 8px)', // Account for gap and padding
            boxSizing: 'border-box'
          }}
        >
          {/* Icon */}
          <div style={{
            width: '32px',
            height: '32px',
            backgroundColor: item.isFolder ? '#e3f2fd' : '#f3e5f5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            flexShrink: 0
          }}>
            {item.isFolder ? '📁' : '📄'}
          </div>
          
          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: '500', 
              fontSize: '14px',
              marginBottom: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {displayText}
            </div>
            {item.brief && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.brief}
              </div>
            )}
          </div>
          
          {/* Meta info */}
          <div style={{ 
            fontSize: '10px', 
            color: '#999',
            textAlign: 'right',
            flexShrink: 0
          }}>
            <div>ID: {item._id}</div>
            {item.qwantaType && <div>Type: {item.qwantaType}</div>}
          </div>
        </div>
        
        {/* Separator - centered in the gap */}
        {hasSeparator && (
          <div style={{
            height: data.itemGap,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '100%',
              height: data.separatorWidth,
              backgroundColor: data.separatorColor
            }} />
          </div>
        )}
      </div>
    );
  };

  // Render individual item
  const renderItem = (item, index) => {
    const isSelected = selectedItems.includes(item._id);
    const processedTemplate = getProcessedTemplateForItem(item._id);
    
    // Template rendering
    if (processedTemplate) {
      return (
        <div
          key={item._id}
          onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
          style={{
            ...getSelectionStyle(isSelected, false, true),
            backgroundColor: getBackgroundColor(isSelected, false),
            padding: '8px',
            marginBottom: '8px',
            borderRadius: '4px',
            cursor: 'pointer',
            border: '1px solid #eee'
          }}
        >
          <QwantaProvider _id={item._id}>
            <DNDEditor
              cardData={processedTemplate}
              enabled={false}
              readOnly={true}
              stackMode={false}
              compact={useCompact}
            />
          </QwantaProvider>
        </div>
      );
    }
    
    // Default rendering
    const displayText = item.title || 
      (item.firstname && item.name ? `${item.firstname} ${item.name}` : 'Untitled');
    
    return (
      <div
        key={item._id}
        onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
        style={{
          ...getSelectionStyle(isSelected, false, true),
          backgroundColor: getBackgroundColor(isSelected, false),
          padding: '12px',
          marginBottom: '8px',
          borderRadius: '4px',
          cursor: 'pointer',
          border: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        {/* Icon */}
        <div style={{
          width: '32px',
          height: '32px',
          backgroundColor: item.isFolder ? '#e3f2fd' : '#f3e5f5',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px'
        }}>
          {item.isFolder ? '📁' : '📄'}
        </div>
        
        {/* Content */}
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontWeight: '500', 
            fontSize: '14px',
            marginBottom: '4px'
          }}>
            {displayText}
          </div>
          {item.brief && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666',
              lineHeight: '1.3'
            }}>
              {item.brief}
            </div>
          )}
        </div>
        
        {/* Meta info */}
        <div style={{ 
          fontSize: '10px', 
          color: '#999',
          textAlign: 'right'
        }}>
          <div>ID: {item._id}</div>
          {item.qwantaType && <div>Type: {item.qwantaType}</div>}
        </div>
      </div>
    );
  };

  // Get display label for a field (from facetConfig or fallback to field name)
  const getFacetLabel = (fieldName) => {
    const config = facetConfig.find(f => f.field === fieldName);
    return config ? config.label : fieldName;
  };

  // Render facets - only show configured facets
  const renderFacets = () => {
    if (Object.keys(facetData).length === 0 || facetConfig.length === 0) return null;
    
    // Filter facetData to only include configured facets
    const configuredFacets = facetConfig
      .map(config => ({
        field: config.field,
        label: config.label,
        values: facetData[config.field] || []
      }))
      .filter(facet => facet.values.length > 0);
    
    if (configuredFacets.length === 0) return null;
    
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #eee',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '8px' }}>Filters:</div>
        {configuredFacets.map(({ field, label, values }) => (
          <div key={field} style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: '500', marginBottom: '4px' }}>{label}:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {values
                .filter(({ value }) => value !== null && value !== undefined && value !== "")
                .slice(0, 5)
                .map(({ value, count }) => (
                <span
                  key={value}
                  style={{
                    padding: '2px 6px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '10px',
                    fontSize: '10px'
                  }}
                >
                  {showFacetCounts ? `${value} (${count})` : value}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search info header */}
      {showSearchInfo && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #eee',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <span style={{ fontWeight: '600' }}>Flat View</span>
            {query && <span style={{ marginLeft: '8px', color: '#666' }}>"{query}"</span>}
            {sortBy && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>sorted by {sortBy} {sortOrder === 1 ? '↑' : '↓'}</span>}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {totalCount} items found
          </div>
        </div>
      )}

      {/* Facets */}
      {showFacets && renderFacets()}

      {/* Error display */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderBottom: '1px solid #eee'
        }}>
          ❌ Search error: {error}
        </div>
      )}

      {/* Items list */}
      <div 
        ref={containerRef}
        style={{ 
          flex: 1, 
          overflow: 'hidden',
          padding: virtualized ? '0' : '16px'
        }}
      >
        {items.length === 0 && !loading && !error && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No results found</div>
            <div style={{ fontSize: '14px' }}>
              {query ? `Try adjusting your search for "${query}"` : 'No items to display'}
            </div>
          </div>
        )}

        {items.length > 0 && (
          virtualized ? (
            // Virtualized rendering - handles unlimited items
            <List
              ref={listRef}
              height={containerHeight}
              itemCount={items.length}
              itemSize={getItemSize}
              itemData={{
                items,
                selectedItems,
                getProcessedTemplateForItem,
                handleItemSelect,
                performSearch,
                hasMore,
                loading,
                useCompact,
                itemHeight,
                itemGap,
                showSeparator,
                separatorWidth,
                separatorColor,
                overscanCount,
                extraContext,
                showDeleteButton
              }}
              overscanCount={overscanCount}
            >
              {VirtualizedRow}
            </List>
          ) : (
            // Non-virtualized fallback for small datasets
            <div style={{ padding: '16px' }}>
              {items.map((item, index) => renderItem(item, index))}
            </div>
          )
        )}

        {/* Loading indicator for non-virtualized mode */}
        {!virtualized && (
          <div 
            ref={loadingRef}
            style={{
              padding: showLoadingState ? '20px' : '1px',
              textAlign: 'center',
              color: '#666',
              height: showLoadingState ? 'auto' : '1px',
              overflow: 'hidden'
            }}
          >
            {showLoadingState && loading && (
              <div>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>🔄 Loading...</div>
                <div style={{ fontSize: '12px' }}>Fetching more results</div>
              </div>
            )}
            {showLoadingState && !loading && !hasMore && items.length > 0 && (
              <div style={{ fontSize: '12px' }}>
                ✅ All {totalCount} items loaded
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default FlatView;